import { CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { assetRepository } from 'api/assetRepository';
import { notificationRepository } from 'api/notificationRepository';
import { VendorName, vendorRepository } from 'api/vendorRepository';
import Card from 'components/card/Card';
import InputField from 'components/fields/InputField';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { flattenZodErrors, notificationSchema } from 'validations/Validations';
import { z } from 'zod';

export default function AddNotificationForm() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    recipient: '',
    message: '',
    vendorId: '',
    assetId: '',
    status: '',
    organizationId: '',
    calibrationId: '',
    reminderCount: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [assets, setAssets] = useState<{ assetId: string; name: string }[]>([]);
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertStatus, setCenterAlertStatus] = useState<"success" | "error" | "info" | "warning">("info");
  const [centerAlertMessage, setCenterAlertMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const vendorData: VendorName[] = await vendorRepository.getVendorNames();
        setVendors(vendorData.map(v => ({ id: v.vendorId, name: v.name })));

        const assetData = await assetRepository.getAssetIdsAndNames();
        setAssets(assetData);

        const organizationData = await assetRepository.getOrganizations();
        setOrganizations(organizationData.map(o => ({ id: o.orgId, name: o.orgName })));

      } catch (error) {
      }
    };
    fetchData();
  }, []);

  // Field-level live validation
  const validateField = (name: string, value: any) => {
    type SchemaKeys = keyof z.infer<typeof notificationSchema>;
    const shape = (notificationSchema as z.ZodObject<any>).shape as Record<string, z.ZodTypeAny>;
    const singleFieldSchema = shape[name as SchemaKeys];
    if (!singleFieldSchema) return;
    const result = singleFieldSchema.safeParse(value);
    setErrors((prev) => ({
      ...prev,
      [name]: result.success ? '' : result.error.issues[0].message,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value);
  };

  const showCenteredAlert = (status: "success" | "error" | "info" | "warning", message: string) => {
    setCenterAlertStatus(status);
    setCenterAlertMessage(message);
    setCenterAlertVisible(true);
    setTimeout(() => setCenterAlertVisible(false), 2000);
  };

  const handleSubmit = async () => {
      if (isSubmitting) return;
  setIsSubmitting(true);
    try {
      notificationSchema.parse(formData);
      const vendorName = vendors.find((v) => v.id === formData.vendorId)?.name || '';
      const assetName = assets.find((a) => a.assetId === formData.assetId)?.name || '';
      const organizationName = organizations.find((o) => o.id === formData.organizationId)?.name || '';
      await notificationRepository.addNotification({
        ...formData,
        delivered: formData.status === 'Delivered',
        vendorName,
        assetName,
        organizationName,
        notifictaionType: '',
        subtype: ''
      });

      showCenteredAlert("success", "Notification added successfully.");
      setTimeout(() => navigate('/admin/notification/NotificationList'), 2500);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrors(flattenZodErrors(error));
        showCenteredAlert("warning", "Please fix the validation errors.");
      } else {
        showCenteredAlert("error", "Failed to add notification.");
      }
    } finally {
    setIsSubmitting(false); 
  }
  };

  const handleClearForm = () => {
    setFormData({
      recipient: '',
      message: '',
      vendorId: '',
      assetId: '',
      status: '',
      organizationId: '',
      calibrationId: '',
      reminderCount: 0,
    });
    setErrors({});
    showCenteredAlert("info", "All fields have been reset.");
    onClose();
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      overflow="hidden"
      align="center"
      justify="flex-start"
      position="relative"
      px={{ base: '20px', md: '40px' }}
      pb="60px"
    >
      {/* Gradient Header */}
      <Box
        position="absolute"
        top="90"
        w="100%"
        h="40vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
        borderRadius="20px"
      />

      {/* Card */}
      <Card
        w={{ sm: '330px', md: '700px', lg: '850px' }}
        p="30px"
        mt={{ md: '150px' }}
        mx="auto"
        zIndex={1}
        overflowY="auto"
        borderRadius="16px"
      >
        <Flex justify="space-between" align="center" mb="20px">
        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
          Add Notification
        </Text>
        <IconButton
          aria-label="Close"
          icon={<CloseIcon />}
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/notification/NotificationList')}
        />
      </Flex>

        <Flex direction="column" w="100%">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
            <Stack direction="column" gap="20px">
              {/* Recipient */}
              <FormControl isInvalid={!!errors['recipient']}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="1px" color={textColor}>Recipient</FormLabel>
                <InputField
                  name="recipient"
                  mb="0px"
                  id="recipient"
                  placeholder="eg. user@example.com"
                  value={formData.recipient}
                  onChange={handleInputChange}
                />
                <FormErrorMessage>{errors['recipient']}</FormErrorMessage>
              </FormControl>
              
              {/* Message */}
              <FormControl isInvalid={!!errors['message']}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>Message</FormLabel>
                <Textarea
                  name="message"
                  placeholder="Enter message"
                  fontSize="sm"
                  value={formData.message}
                  onChange={handleInputChange}
                />
                <FormErrorMessage>{errors['message']}</FormErrorMessage>
              </FormControl>
            </Stack>

            <Stack direction="column" gap="20px">
              {/* Vendor */}
              <FormControl isInvalid={!!errors['vendorId']}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>Vendor</FormLabel>
                <Select
                  name="vendorId"
                  placeholder="Select vendor"
                  h="44px"
                  fontSize="sm"
                  value={formData.vendorId}
                  onChange={handleInputChange}
                >
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors['vendorId']}</FormErrorMessage>
              </FormControl>

              {/* Asset */}
              <FormControl isInvalid={!!errors['assetId']}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>Asset</FormLabel>
                <Select
                  name="assetId"
                  placeholder="Select asset"
                  h="44px"
                  fontSize="sm"
                  value={formData.assetId}
                  onChange={handleInputChange}
                >
                  {assets.map((a) => (
                    <option key={a.assetId} value={a.assetId}>
                      {a.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors['assetId']}</FormErrorMessage>
              </FormControl>

              {/* Organization */}
              <FormControl isInvalid={!!errors['organizationId']}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>Organization</FormLabel>
                <Select
                  name="organizationId"
                  placeholder="Select organization"
                  h="44px"
                  fontSize="sm"
                  value={formData.organizationId}
                  onChange={handleInputChange}
                >
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors['organizationId']}</FormErrorMessage>
              </FormControl>
            </Stack>
          </SimpleGrid>

          {/* Buttons */}
          <Flex justify="center" gap="20px" mt="24px">
            <Button
              variant="outline"
              fontSize="sm"
              borderRadius="16px"
              w={{ base: '128px', md: '148px' }}
              h="46px"
              onClick={onOpen}
            >
              Clear
            </Button>
            <Button
              variant="darkBrand"
              fontSize="sm"
              borderRadius="16px"
              w={{ base: '128px', md: '148px' }}
              h="46px"
              onClick={handleSubmit}
               isLoading={isSubmitting}
              isDisabled={isSubmitting}
            >
              Submit
            </Button>
          </Flex>
        </Flex>
      </Card>


      {/* Delete Confirmation Dialog */}
      <AlertDialog 
        isOpen={isOpen} 
        leastDestructiveRef={cancelRef} 
        onClose={onClose} 
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Clear Form
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to clear all fields? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleClearForm} ml={3}>
                Clear
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Centered Alert */}
      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="lg"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Flex>
  );
}
